import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Eye, Search, Loader2, Phone, Mail, MapPin, Trash2, X, User, Minus, DollarSign, Package, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/SearchableSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-orange-500/20 text-orange-500',
  confirmed: 'bg-blue-500/20 text-blue-500',
  shipped: 'bg-purple-500/20 text-purple-500',
  delivered: 'bg-green-500/20 text-green-500',
  cancelled: 'bg-red-500/20 text-red-500'
};

const STATUS_LABELS = {
  pending: 'Pendiente', 
  confirmed: 'Confirmado', 
  shipped: 'Enviado', 
  delivered: 'Entregado', 
  cancelled: 'Cancelado'
};

const PAYMENT_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-500',
  paid: 'bg-green-500/20 text-green-500'
};

const PAYMENT_LABELS = {
  pending: 'Pendiente',
  paid: 'Pagado'
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    items: [],
    shipping_cost: 0,
    notes: '',
    payment_status: 'pending',
    order_status: 'pending'
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);

  useEffect(() => { 
    fetchOrders(); 
    fetchProducts();
  }, [searchQuery, statusFilter]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('order_status', statusFilter);
      const res = await axios.get(`${API}/orders?${params}`);
      setOrders(res.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products?status=active&sort_by=created_at&sort_order=desc&skip=0&limit=500`);
      setProducts(res.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const updateOrderStatus = async (orderId, field, value) => {
    try {
      const params = new URLSearchParams();
      params.append(field, value);
      await axios.patch(`${API}/orders/${orderId}/status?${params}`);
      toast.success('Estado actualizado');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, [field]: value }));
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de eliminar este pedido?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Pedido eliminado');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Error al eliminar pedido');
    }
  };

  const openCreateModal = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      items: [],
      shipping_cost: 0,
      notes: '',
      payment_status: 'pending',
      order_status: 'pending'
    });
    setSelectedProduct('');
    setSelectedVariant('');
    setItemQuantity(1);
    setIsCreateModalOpen(true);
  };

  const addItemToOrder = () => {
    if (!selectedProduct || !selectedVariant) {
      toast.error('Selecciona un producto y variante');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    const [size, color] = selectedVariant.split('|||');
    const variant = product.variants.find(v => v.size === size && v.color === color);

    if (!variant) {
      toast.error('Variante no encontrada');
      return;
    }

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      size: size,
      color: color,
      quantity: itemQuantity,
      price: product.price,
      image_url: product.images?.[0] || ''
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setSelectedProduct('');
    setSelectedVariant('');
    setItemQuantity(1);
    toast.success('Producto agregado');
  };

  const removeItemFromOrder = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItemQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, quantity: newQuantity } : item
      )
    }));
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + (parseFloat(formData.shipping_cost) || 0);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Nombre y teléfono son requeridos');
      return;
    }
    
    if (formData.items.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    setSaving(true);
    try {
      const orderData = {
        ...formData,
        subtotal: calculateSubtotal(),
        total: calculateTotal(),
        shipping_cost: parseFloat(formData.shipping_cost) || 0
      };

      await axios.post(`${API}/orders`, orderData);
      toast.success('Pedido creado exitosamente');
      setIsCreateModalOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear pedido');
    } finally {
      setSaving(false);
    }
  };

  // Get product options for searchable select
  const productOptions = products.map(p => ({
    id: p.id,
    name: p.name,
    price: p.price,
    image: p.images?.[0],
    category: p.category_name
  }));

  // Get variant options for selected product
  const getVariantOptions = () => {
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return [];
    return product.variants?.filter(v => v.stock_quantity > 0).map(v => ({
      value: `${v.size}|||${v.color}`,
      label: `${v.size} / ${v.color} (${v.stock_quantity} disp.)`
    })) || [];
  };

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-white">Pedidos</h1>
          <p className="text-white/60 text-sm">{orders.length} pedidos</p>
        </div>
        <Button onClick={openCreateModal} className="btn-gold-solid" data-testid="create-order-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Pedido
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre, teléfono o número de pedido..."
            className="pl-10 bg-[#121212] border-white/10 text-white"
            data-testid="order-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/20">
            <SelectItem value="all" className="text-white">Todos</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-white">{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-4 px-4 text-left text-white/60 text-sm font-medium">Pedido</th>
                <th className="py-4 px-4 text-left text-white/60 text-sm font-medium hidden md:table-cell">Cliente</th>
                <th className="py-4 px-4 text-right text-white/60 text-sm font-medium">Total</th>
                <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Estado</th>
                <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Pago</th>
                <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`order-row-${order.id}`}>
                  <td className="py-4 px-4">
                    <p className="text-gold font-medium">{order.order_number}</p>
                    <p className="text-white/40 text-xs">
                      {new Date(order.created_at).toLocaleDateString('es-VE', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </td>
                  <td className="py-4 px-4 hidden md:table-cell">
                    <p className="text-white">{order.customer_name}</p>
                    <p className="text-white/40 text-xs">{order.customer_phone}</p>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-gold font-semibold">${order.total?.toFixed(2)}</span>
                    <p className="text-white/40 text-xs">{order.items?.length || 0} items</p>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Select value={order.order_status} onValueChange={(v) => updateOrderStatus(order.id, 'order_status', v)}>
                      <SelectTrigger className={`w-32 border-0 ${STATUS_COLORS[order.order_status] || 'bg-gray-500/20 text-gray-400'}`}>
                        <SelectValue>{STATUS_LABELS[order.order_status] || order.order_status}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k} className="text-white">{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Select value={order.payment_status} onValueChange={(v) => updateOrderStatus(order.id, 'payment_status', v)}>
                      <SelectTrigger className={`w-28 border-0 ${PAYMENT_COLORS[order.payment_status] || 'bg-gray-500/20 text-gray-400'}`}>
                        <SelectValue>{PAYMENT_LABELS[order.payment_status] || order.payment_status}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20">
                        <SelectItem value="pending" className="text-white">Pendiente</SelectItem>
                        <SelectItem value="paid" className="text-white">Pagado</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button 
                        onClick={() => setSelectedOrder(order)} 
                        className="p-2 text-white/40 hover:text-gold transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => deleteOrder(order.id)} 
                        className="p-2 text-white/40 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && (
            <div className="text-center py-12">
              <p className="text-white/40 mb-4">No hay pedidos</p>
              <Button onClick={openCreateModal} className="btn-gold">
                Crear primer pedido
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-3xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto p-0">
          <div className="p-4 sm:p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-gold" />
                Nuevo Pedido Manual
              </DialogTitle>
            </DialogHeader>
          </div>

          <form onSubmit={handleCreateOrder} className="flex flex-col">
            <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Section 1: Cliente */}
              <div className="flex gap-3 sm:gap-4">
                {/* Step indicator */}
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold shrink-0 ${formData.customer_name && formData.customer_phone ? 'bg-gold text-black' : 'bg-white/10 text-white/60'}`}>
                    1
                  </div>
                  <div className={`w-0.5 flex-1 mt-2 ${formData.customer_name && formData.customer_phone ? 'bg-gold' : 'bg-white/10'}`} />
                </div>
                {/* Content */}
                <div className="flex-1 pb-2 min-w-0">
                  <h3 className="text-white font-semibold text-base">Datos del Cliente</h3>
                  <p className="text-white/40 text-xs sm:text-sm mb-4">Información de contacto y envío</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="relative">
                      <label className="text-white/60 text-sm">Nombre <span className="text-red-400">*</span></label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <Input
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          required
                          className="bg-[#121212] border-white/10 text-white rounded-xl pl-10"
                          placeholder="Nombre del cliente"
                          data-testid="customer-name-input"
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label className="text-white/60 text-sm">Teléfono <span className="text-red-400">*</span></label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <Input
                          value={formData.customer_phone}
                          onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                          required
                          className="bg-[#121212] border-white/10 text-white rounded-xl pl-10"
                          placeholder="+58 424 XXX XXXX"
                          data-testid="customer-phone-input"
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label className="text-white/60 text-sm">Email</label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <Input
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          className="bg-[#121212] border-white/10 text-white rounded-xl pl-10"
                          placeholder="cliente@email.com"
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label className="text-white/60 text-sm">Dirección</label>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <Input
                          value={formData.customer_address}
                          onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                          className="bg-[#121212] border-white/10 text-white rounded-xl pl-10"
                          placeholder="Dirección de envío"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Productos */}
              <div className="flex gap-3 sm:gap-4">
                {/* Step indicator */}
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold shrink-0 ${formData.items.length > 0 ? 'bg-gold text-black' : 'bg-white/10 text-white/60'}`}>
                    2
                  </div>
                  <div className={`w-0.5 flex-1 mt-2 ${formData.items.length > 0 ? 'bg-gold' : 'bg-white/10'}`} />
                </div>
                {/* Content */}
                <div className="flex-1 pb-2 min-w-0">
                  <h3 className="text-white font-semibold text-base">Productos del Pedido</h3>
                  <p className="text-white/40 text-xs sm:text-sm mb-4">Busca y agrega los productos</p>

                  {/* Product Search */}
                  <SearchableSelect
                    options={productOptions}
                    value={selectedProduct}
                    onChange={(val) => { setSelectedProduct(val); setSelectedVariant(''); }}
                    placeholder="Buscar producto por nombre..."
                    searchPlaceholder="Escribir para buscar..."
                    getOptionValue={(opt) => opt.id}
                    getOptionLabel={(opt) => `${opt.name} - $${opt.price.toFixed(2)}`}
                    renderOption={(opt) => (
                      <div className="flex items-center gap-3">
                        {opt.image && (
                          <img src={opt.image} alt="" className="w-10 h-10 object-cover rounded-lg" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{opt.name}</p>
                          <p className="text-xs text-white/40">{opt.category} • ${opt.price.toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                  />

                  {/* Selected product card */}
                  <AnimatePresence>
                    {selectedProduct && (() => {
                      const product = products.find(p => p.id === selectedProduct);
                      if (!product) return null;
                      return (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="mt-4 p-4 rounded-xl border border-white/10 bg-[#121212]"
                        >
                          <div className="flex items-start gap-4 mb-4">
                            {product.images?.[0] && (
                              <img src={product.images[0]} alt="" className="w-16 h-16 object-cover rounded-lg" />
                            )}
                            <div className="flex-1">
                              <p className="text-white font-medium">{product.name}</p>
                              <p className="text-gold text-sm font-semibold">${product.price.toFixed(2)}</p>
                            </div>
                          </div>

                          {/* Variant pills */}
                          <div className="mb-4">
                            <label className="text-white/60 text-sm mb-2 block">Variante</label>
                            <div className="flex flex-wrap gap-2">
                              {getVariantOptions().map((v) => (
                                <button
                                  key={v.value}
                                  type="button"
                                  onClick={() => setSelectedVariant(v.value)}
                                  className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                                    selectedVariant === v.value
                                      ? 'bg-gold/20 border-gold text-gold'
                                      : 'border-white/20 text-white/60 hover:border-white/40'
                                  }`}
                                >
                                  {v.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Quantity stepper */}
                          <div className="flex items-center justify-between mb-4">
                            <label className="text-white/60 text-sm">Cantidad</label>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setItemQuantity(Math.max(1, itemQuantity - 1))}
                                className="w-10 h-10 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-8 text-center text-white font-semibold text-lg">{itemQuantity}</span>
                              <button
                                type="button"
                                onClick={() => setItemQuantity(itemQuantity + 1)}
                                className="w-10 h-10 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Add button */}
                          <Button
                            type="button"
                            onClick={addItemToOrder}
                            className="w-full bg-gold hover:bg-gold-hover text-black font-medium"
                            disabled={!selectedVariant}
                          >
                            <Plus className="w-4 h-4 mr-2" /> Agregar al pedido
                          </Button>
                        </motion.div>
                      );
                    })()}
                  </AnimatePresence>

                  {/* Items List */}
                  <div className="mt-4 space-y-3">
                    <AnimatePresence>
                      {formData.items.length === 0 ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex flex-col items-center justify-center py-8 text-white/30"
                        >
                          <Package className="w-10 h-10 mb-3" />
                          <p className="text-sm">No hay productos agregados</p>
                        </motion.div>
                      ) : (
                        formData.items.map((item, index) => (
                          <motion.div
                            key={`${item.product_id}-${item.size}-${item.color}-${index}`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                            className="p-3 rounded-xl border border-white/10 bg-[#121212] space-y-3"
                          >
                            {/* Product info row */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0">
                                {item.image_url ? (
                                  <img src={item.image_url} alt="" className="w-11 h-11 sm:w-12 sm:h-12 object-cover rounded-lg flex-shrink-0" />
                                ) : (
                                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                                    <Package className="w-5 h-5 text-white/20" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="text-white text-sm font-bold truncate">{item.product_name}</p>
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs">{item.size}</span>
                                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs">{item.color}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeItemFromOrder(index)}
                                className="p-1.5 rounded-full text-white/40 hover:text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {/* Quantity + price row */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-8 text-center text-white font-semibold">{item.quantity}</span>
                                <button
                                  type="button"
                                  onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="text-gold font-semibold">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </motion.div>
                        ))
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Section 3: Detalles */}
              <div className="flex gap-3 sm:gap-4">
                {/* Step indicator */}
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold shrink-0 ${formData.items.length > 0 ? 'bg-gold text-black' : 'bg-white/10 text-white/60'}`}>
                    3
                  </div>
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-base">Detalles y Pago</h3>
                  <p className="text-white/40 text-xs sm:text-sm mb-4">Configura envío, estado y notas</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="relative">
                      <label className="text-white/60 text-sm">Costo de Envío ($)</label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.shipping_cost}
                          onChange={(e) => setFormData({ ...formData, shipping_cost: e.target.value })}
                          className="bg-[#121212] border-white/10 text-white rounded-xl pl-10"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-white/60 text-sm">Estado del Pedido</label>
                      <Select value={formData.order_status} onValueChange={(v) => setFormData({ ...formData, order_status: v })}>
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white mt-1 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/20">
                          {Object.entries(STATUS_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k} className="text-white">{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-white/60 text-sm">Estado del Pago</label>
                      <Select value={formData.payment_status} onValueChange={(v) => setFormData({ ...formData, payment_status: v })}>
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white mt-1 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/20">
                          <SelectItem value="pending" className="text-white">Pendiente</SelectItem>
                          <SelectItem value="paid" className="text-white">Pagado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-white/60 text-sm">Notas</label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-[#121212] border-white/10 text-white mt-1 rounded-xl"
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Totals Bar + Actions */}
            <AnimatePresence>
              {formData.items.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.25 }}
                  className="sticky bottom-0 bg-[#121212] border-t border-white/10 p-4 mt-2"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <span className="text-white/60 text-sm">
                      {formData.items.length} {formData.items.length === 1 ? 'producto' : 'productos'}
                    </span>
                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                      <div className="text-left sm:text-right">
                        <p className="text-white/60 text-xs">Subtotal</p>
                        <p className="text-white text-sm font-medium">${calculateSubtotal().toFixed(2)}</p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-white/60 text-xs">Envío</p>
                        <p className="text-white text-sm font-medium">${(parseFloat(formData.shipping_cost) || 0).toFixed(2)}</p>
                      </div>
                      <div className="w-px h-8 bg-white/10 hidden sm:block" />
                      <div className="text-right">
                        <p className="text-white/60 text-xs">Total</p>
                        <p className="text-gold text-lg font-bold">${calculateTotal().toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 sm:gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateModalOpen(false)}
                      className="flex-1 border-white/20 text-white hover:bg-white/5 rounded-xl h-11 sm:h-9"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving || formData.items.length === 0}
                      className="flex-1 btn-gold-solid rounded-xl h-11 sm:h-9"
                      data-testid="submit-order-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Pedido'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions when no items (totals bar hidden) */}
            {formData.items.length === 0 && (
              <div className="p-4 sm:p-6 pt-0">
                <div className="flex gap-3 sm:gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="flex-1 border-white/20 text-white hover:bg-white/5 rounded-xl h-11 sm:h-9"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={true}
                    className="flex-1 btn-gold-solid rounded-xl h-11 sm:h-9"
                    data-testid="submit-order-btn"
                  >
                    Crear Pedido
                  </Button>
                </div>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center justify-between">
              <span>Pedido {selectedOrder?.order_number}</span>
              <span className={`text-xs px-2 py-1 ${STATUS_COLORS[selectedOrder?.order_status]}`}>
                {STATUS_LABELS[selectedOrder?.order_status]}
              </span>
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6 mt-4">
              <div className="p-4 bg-white/5 space-y-2">
                <h4 className="text-gold text-sm font-medium mb-3">Información del Cliente</h4>
                <p className="text-white flex items-center gap-2">
                  <span className="text-white/60">Nombre:</span> {selectedOrder.customer_name}
                </p>
                <p className="text-white flex items-center gap-2">
                  <Phone className="w-4 h-4 text-white/40" /> {selectedOrder.customer_phone}
                </p>
                {selectedOrder.customer_email && (
                  <p className="text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-white/40" /> {selectedOrder.customer_email}
                  </p>
                )}
                {selectedOrder.customer_address && (
                  <p className="text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-white/40" /> {selectedOrder.customer_address}
                  </p>
                )}
              </div>

              <div>
                <h4 className="text-gold text-sm font-medium mb-3">Productos</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-white/5">
                      <div>
                        <p className="text-white">{item.product_name}</p>
                        <p className="text-white/40 text-xs">
                          Talla: {item.size} | Color: {item.color} | x{item.quantity}
                        </p>
                      </div>
                      <p className="text-white">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-gold/10 border border-gold/20">
                <div className="flex justify-between mb-2">
                  <span className="text-white/60">Subtotal:</span>
                  <span className="text-white">${selectedOrder.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-white/60">Envío:</span>
                  <span className="text-white">${selectedOrder.shipping_cost?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg">
                  <span className="text-gold">Total:</span>
                  <span className="text-gold">${selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="p-4 bg-white/5">
                  <h4 className="text-white/60 text-sm mb-2">Notas:</h4>
                  <p className="text-white text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="space-y-3">
                <a
                  href={`https://wa.me/${selectedOrder.customer_phone?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${selectedOrder.customer_name}! Sobre tu pedido ${selectedOrder.order_number}...`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-[#25D366] text-white text-center py-3 font-medium hover:bg-[#20BA5C] transition-colors"
                >
                  Contactar por WhatsApp
                </a>
                <button
                  onClick={() => deleteOrder(selectedOrder.id)}
                  className="w-full py-3 border border-red-500/50 text-red-500 hover:bg-red-500/10 transition-colors"
                >
                  Eliminar Pedido
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
